import { ChartOption } from '../types/chart-option.types';
import { IntervalType } from '../types/data-set.types';

// -----------------------------------------------------------------------------
export interface AgeRangeOption {
  id: string;
  displayName: string;
  datasetId: {
    male: string;
    female: string;
  };
  ageFilter?: (age: number, intervalType: IntervalType) => boolean;
  lengthFilter?: (length: number) => boolean;
  measurementType?: string;
}

// -----------------------------------------------------------------------------
export const heightAgeRangeOptions: ChartOption[] = [
  {
    id: 'weekly-0-13-height',
    displayName: '0-13 weeks (weekly)',
    datasetId: {
      male: 'who-boys-length-weekly',
      female: 'who-girls-length-weekly',
    },
    ageFilter: (age, intervalType) =>
      intervalType === 'week' && age >= 0 && age <= 13,
    measurementType: 'length',
  },
  {
    id: 'monthly-0-6-height',
    displayName: '0-6 months',
    datasetId: {
      male: 'who-boys-height-monthly',
      female: 'who-girls-height-monthly',
    },
    ageFilter: (age) => age >= 0 && age <= 6,
    measurementType: 'length',
  },
  {
    id: 'monthly-6-24-height',
    displayName: '6-24 months',
    datasetId: {
      male: 'who-boys-height-monthly',
      female: 'who-girls-height-monthly',
    },
    ageFilter: (age) => age >= 6 && age <= 24,
    measurementType: 'length',
  },
  {
    id: 'monthly-0-24-height',
    displayName: '0-2 years',
    datasetId: {
      male: 'who-boys-height-monthly',
      female: 'who-girls-height-monthly',
    },
    ageFilter: (age) => age >= 0 && age <= 24,
    measurementType: 'length',
  },
  {
    id: 'monthly-24-60-height',
    displayName: '2-5 years',
    datasetId: {
      male: 'who-boys-height-monthly',
      female: 'who-girls-height-monthly',
    },
    ageFilter: (age) => age >= 24 && age <= 60,
    measurementType: 'length',
  },
  {
    id: 'monthly-0-60-height',
    displayName: '0-5 years',
    datasetId: {
      male: 'who-boys-height-monthly',
      female: 'who-girls-height-monthly',
    },
    ageFilter: (age) => age >= 0 && age <= 60,
    measurementType: 'length',
  },
];

// -----------------------------------------------------------------------------
export const weightAgeRangeOptions: ChartOption[] = [
  {
    id: 'weekly-0-13-weight',
    displayName: '0-13 weeks (weekly)',
    datasetId: {
      male: 'who-boys-weight-weekly',
      female: 'who-girls-weight-weekly',
    },
    ageFilter: (age, intervalType) =>
      intervalType === 'week' && age >= 0 && age <= 13,
    measurementType: 'weight',
  },
  {
    id: 'monthly-0-6-weight',
    displayName: '0-6 months',
    datasetId: {
      male: 'who-boys-weight-monthly',
      female: 'who-girls-weight-monthly',
    },
    ageFilter: (age) => age >= 0 && age <= 6,
    measurementType: 'weight',
  },
  {
    id: 'monthly-6-24-weight',
    displayName: '6-24 months',
    datasetId: {
      male: 'who-boys-weight-monthly',
      female: 'who-girls-weight-monthly',
    },
    ageFilter: (age) => age >= 6 && age <= 24,
    measurementType: 'weight',
  },
  {
    id: 'monthly-0-24-weight',
    displayName: '0-2 years',
    datasetId: {
      male: 'who-boys-weight-monthly',
      female: 'who-girls-weight-monthly',
    },
    ageFilter: (age) => age >= 0 && age <= 24,
    measurementType: 'weight',
  },
  {
    id: 'monthly-24-60-weight',
    displayName: '2-5 years',
    datasetId: {
      male: 'who-boys-weight-monthly',
      female: 'who-girls-weight-monthly',
    },
    ageFilter: (age) => age >= 24 && age <= 60,
    measurementType: 'weight',
  },
  {
    id: 'monthly-0-60-weight',
    displayName: '0-5 years',
    datasetId: {
      male: 'who-boys-weight-monthly',
      female: 'who-girls-weight-monthly',
    },
    ageFilter: (age) => age >= 0 && age <= 60,
    measurementType: 'weight',
  },
];

// -----------------------------------------------------------------------------
export const bmiAgeRangeOptions: ChartOption[] = [
  {
    id: 'monthly-0-24-bmi',
    displayName: '0-2 years',
    datasetId: {
      male: 'who-boys-bmi-monthly-0-5',
      female: 'who-girls-bmi-monthly-0-5',
    },
    ageFilter: (age) => age >= 0 && age <= 24,
    measurementType: 'bmi',
  },
  {
    id: 'monthly-24-60-bmi',
    displayName: '2-5 years',
    datasetId: {
      male: 'who-boys-bmi-monthly-0-5',
      female: 'who-girls-bmi-monthly-0-5',
    },
    ageFilter: (age) => age >= 24 && age <= 60,
    measurementType: 'bmi',
  },
  {
    id: 'monthly-0-60-bmi',
    displayName: '0-5 years',
    datasetId: {
      male: 'who-boys-bmi-monthly-0-5',
      female: 'who-girls-bmi-monthly-0-5',
    },
    ageFilter: (age) => age >= 0 && age <= 60,
    measurementType: 'bmi',
  },
];

// -----------------------------------------------------------------------------
export const headCircumferenceAgeRangeOptions: ChartOption[] = [
  {
    id: 'weekly-0-13-head',
    displayName: '0-13 weeks (weekly)',
    datasetId: {
      male: 'who-boys-head-circumference-weekly-0-13',
      female: 'who-girls-head-circumference-weekly-0-13',
    },
    ageFilter: (age, intervalType) =>
      intervalType === 'week' && age >= 0 && age <= 13,
    measurementType: 'head_circumference',
  },
  {
    id: 'monthly-0-24-head',
    displayName: '0-2 years',
    datasetId: {
      male: 'who-boys-head-circumference-monthly-0-5',
      female: 'who-girls-head-circumference-monthly-0-5',
    },
    ageFilter: (age) => age >= 0 && age <= 24,
    measurementType: 'head_circumference',
  },
  {
    id: 'monthly-0-60-head',
    displayName: '0-5 years',
    datasetId: {
      male: 'who-boys-head-circumference-monthly-0-5',
      female: 'who-girls-head-circumference-monthly-0-5',
    },
    ageFilter: (age) => age >= 0 && age <= 60,
    measurementType: 'head_circumference',
  },
];
